// Shared helpers for handling dropped files/folders, including RED .RDC folders
// (which must keep their structure in S3). Used by the owner Uploader and the
// guest drop-upload page.

export const sanitizeSeg = (s) => s.replace(/[^\w.\-]+/g, '_');
export const sanitizePath = (p) => p.split('/').map(sanitizeSeg).filter(Boolean).join('/');

// The `.RDC` segment in a relative path, if any (RED clip folder).
const RDC_RE = /(?:^|\/)([^/]+\.rdc)(?:\/|$)/i;
export const rdcSegmentOf = (relPath) => {
  const m = RDC_RE.exec(relPath || '');
  return m ? m[1] : null;
};
// Path inside the .RDC folder (the part after the .RDC segment).
export const innerOfRdc = (relPath, seg) => {
  const idx = relPath.toLowerCase().indexOf(seg.toLowerCase());
  return relPath.slice(idx + seg.length).replace(/^\/+/, '');
};
// Natural-ish sort so spanned parts (_001, _002…) order correctly.
export const byName = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true });

// Recursively read a dropped DataTransfer entry into {file, relPath} items.
export async function readEntry(entry, prefix, out) {
  if (!entry) return;
  if (entry.isFile) {
    await new Promise((res) => {
      entry.file(
        (f) => {
          out.push({ file: f, relPath: prefix + entry.name });
          res();
        },
        () => res()
      );
    });
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    const childPrefix = `${prefix}${entry.name}/`;
    const readBatch = () =>
      new Promise((res) => reader.readEntries((e) => res(e || []), () => res([])));
    // readEntries returns in batches; loop until empty.
    // eslint-disable-next-line no-await-in-loop
    for (let batch = await readBatch(); batch.length; batch = await readBatch()) {
      for (const e of batch) {
        // eslint-disable-next-line no-await-in-loop
        await readEntry(e, childPrefix, out);
      }
    }
  }
}

// Read dropped DataTransfer items (files and/or folders) into {file, relPath}[].
export async function readDataTransfer(dt) {
  const handles = dt.items
    ? Array.from(dt.items)
        .map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
        .filter(Boolean)
    : [];
  if (handles.length) {
    const out = [];
    for (const en of handles) {
      // eslint-disable-next-line no-await-in-loop
      await readEntry(en, '', out);
    }
    if (out.length) return out;
  }
  return dt.files?.length
    ? Array.from(dt.files).map((f) => ({ file: f, relPath: f.webkitRelativePath || f.name }))
    : [];
}

// Split {file, relPath}[] into RED .RDC folder groups + loose files.
// Each group: { folderName, relPrefix, files:[{file, inner}], totalSize,
// primaryInner } where `inner` is the path inside the .RDC folder. Callers map
// these to S3 keys (owner builds keys directly; guest lets the backend build
// them under the drop prefix).
export function groupRdcEntries(entries) {
  const groupsByPrefix = new Map();
  const loose = [];
  for (const { file, relPath } of entries) {
    const seg = rdcSegmentOf(relPath);
    if (seg) {
      const before = relPath.slice(0, relPath.toLowerCase().indexOf(seg.toLowerCase()));
      const groupId = `${seg}::${before}`;
      let g = groupsByPrefix.get(groupId);
      if (!g) {
        g = { folderName: seg, files: [], totalSize: 0 };
        groupsByPrefix.set(groupId, g);
      }
      const inner = innerOfRdc(relPath, seg) || file.name;
      g.files.push({ file, inner });
      g.totalSize += file.size;
    } else {
      loose.push({ file, relPath: relPath || file.name });
    }
  }
  const groups = [...groupsByPrefix.values()].map((g) => {
    g.files.sort((a, b) => byName(a.file, b.file));
    const r3ds = g.files.filter((p) => /\.r3d$/i.test(p.file.name));
    g.primaryInner = (r3ds[0] || g.files[0]).inner;
    return g;
  });
  return { groups, loose };
}
